'use client';

import { createTheme } from '@mui/material/styles';
import { HelveticaNeue } from '@peerless/ui-assets/fonts';
import { palette } from '@peerless/ui-assets/palette';

import '@aws-amplify/ui-react/styles.css';

/** Static import of fontawesome css
 * See https://docs.fontawesome.com/web/use-with/react/use-with
 */
import { config } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
config.autoAddCss = false;

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    'nav-bar': true;
    'nav-menu-item': true;
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    cardHover: true;
    cardBorder: true;
  }
}

const defaults = createTheme({ palette });

/**
 * MUI Theme is used for all MUI components
 */
export const theme = createTheme({
  palette,
  shape: { borderRadius: 0 },
  typography: {
    fontFamily: HelveticaNeue.style.fontFamily,
    h1: {
      fontSize: '3rem',
      fontWeight: '700',
      marginBottom: '1rem',
      [defaults.breakpoints.down('xl')]: {
        fontSize: '2rem',
      },
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '1rem',
      [defaults.breakpoints.down('xl')]: {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: '700',
      marginBottom: '1rem',
      [defaults.breakpoints.down('xl')]: {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: '700',
      marginBottom: '1rem',
      [defaults.breakpoints.down('xl')]: {
        fontSize: '1.125rem',
      },
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: '700',
      marginBottom: '1rem',
      [defaults.breakpoints.down('xl')]: {
        fontSize: '1rem',
      },
    },
    subtitle1: {
      fontSize: '.75rem',
    },
    subtitle2: {
      fontSize: '.75rem',
      fontWeight: '700',
    },
    body1: {
      fontSize: '1.125rem',
      [defaults.breakpoints.down('xl')]: {
        fontSize: '1rem',
      },
    },
    body2: {
      fontSize: '1rem',
      [defaults.breakpoints.down('xl')]: {
        fontSize: '.875rem',
      },
    },
  },
  components: {
    /** Global Baseline CSS */
    MuiCssBaseline: {
      styleOverrides: themeParam => ({
        body: {
          font: themeParam.typography.fontFamily,

          fontSize: '1.125rem',
          [defaults.breakpoints.down('xl')]: {
            fontSize: '1rem',
          },

          '& h1, h2, h3, h4, h5, h6, p': {
            margin: '0 0 1rem 0',
          },

          '.header-underline-left': {
            position: 'relative',
            paddingBottom: '.5rem',
            '&::after': {
              width: 80,
              height: 4,
              backgroundColor: palette.primary.main,
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
            },
          },

          '.header-underline-center': {
            position: 'relative',
            paddingBottom: '.5rem',
            '&::after': {
              width: 80,
              height: 4,
              backgroundColor: palette.primary.main,
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              marginLeft: -40,
            },
          },

          '.header-underline-right': {
            position: 'relative',
            paddingBottom: '.5rem',
            '&::after': {
              width: 80,
              height: 4,
              backgroundColor: palette.primary.main,
              content: '""',
              position: 'absolute',
              bottom: 0,
              right: 0,
            },
          },

          '.responsive-video': {
            display: 'block',
            maxWidth: '100%',
            '& iframe': {
              width: '100%',
              aspectRatio: '16 / 9',
              border: '0',
            },
          },

          '.btn-root': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '5px 15px',
            border: '4px solid',
            textDecoration: 'none',
            textTransform: 'uppercase',
            minHeight: '3rem',
            minWidth: '3rem',
            width: '100%',
            fontSize: '1.125rem',
            fontWeight: 500,
            textAlign: 'center',
            transition: 'background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease',
            ':hover': {
              textDecoration: 'none',
            },
          },
          '.btn-primary': {
            color: palette.primary.main,
            borderColor: palette.primary.main,
            backgroundColor: palette.primary.contrastText,
            ':hover': {
              color: palette.primary.contrastText,
              backgroundColor: palette.primary.main,
            },
          },
          '.btn-secondary': {
            color: palette.secondary.dark,
            borderColor: palette.secondary.dark,
            backgroundColor: palette.primary.contrastText,
            ':hover': {
              color: palette.primary.contrastText,
              backgroundColor: palette.secondary.dark,
            },
          },
          '.btn-primary.btn-filled': {
            color: palette.primary.contrastText,
            backgroundColor: palette.primary.main,
            ':hover': {
              backgroundColor: palette.secondary.dark,
              borderColor: palette.secondary.dark,
            },
          },
          '.btn-secondary.btn-filled': {
            color: palette.primary.contrastText,
            backgroundColor: palette.secondary.dark,
            ':hover': {
              backgroundColor: palette.primary.main,
              borderColor: palette.primary.main,
            },
          },
        },
        a: {
          color: palette.primary.main,
          textDecoration: 'none',
          ':hover': {
            textDecoration: 'underline',
          },
        },
        h1: {
          ...themeParam.typography.h1,
        },
        h2: {
          ...themeParam.typography.h2,
        },
        h3: {
          ...themeParam.typography.h3,
        },
        h4: {
          ...themeParam.typography.h4,
        },
        h5: {
          ...themeParam.typography.h5,
        },
      }),
    },
    /*! typography */
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.header-underline': {
            position: 'relative',
            paddingBottom: '.5rem',
            '&::after': {
              width: 80,
              height: 4,
              backgroundColor: palette.primary.main,
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
            },
            '&.MuiTypography-alignCenter::after': {
              left: '50%',
              marginLeft: -40,
            },
          },
        },
      },
    },

    /*! main nav links */
    MuiLink: {
      styleOverrides: {
        root: {
          color: palette.primary.main,
          textDecoration: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'none',
          },
        },
      },
      variants: [
        {
          props: { variant: 'nav-bar' },
          style: {
            fontFamily: HelveticaNeue.style.fontFamily,
            fontSize: '.85rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            transition: 'color .25s ease .1s,',
            color: 'text.primary',
            '&:hover': { color: palette.primary.main },
          },
        },
        {
          props: { variant: 'nav-menu-item' },
          style: {
            fontFamily: HelveticaNeue.style.fontFamily,
            fontSize: '.75rem',
            fontWeight: 'normal',
            marginBottom: '0.5rem',
            transition: 'color .25s ease .1s,',
            '&:hover': { color: palette.primary.main },
          },
        },
      ],
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          '[role="menuitem"]': {
            fontFamily: HelveticaNeue.style.fontFamily,
            fontSize: '.75rem',
            fontWeight: 'normal',
            marginBottom: '0.5rem',
            transition: 'color .25s ease .1s,',
          },
        },
      },
    },

    /*! buttons */
    MuiButton: {
      defaultProps: {
        variant: 'contained',
      },
      styleOverrides: {
        root: {
          minHeight: '3rem',
          minWidth: '3rem',
          fontSize: '1.125rem',
        },
        contained: {
          borderWidth: '4px',
          boxShadow: 'none',
          '&:hover': {
            borderWidth: '4px',
            boxShadow: 'none',
          },
          '& .MuiCircularProgress-svg': {
            color: palette.primary.contrastText,
          },
        },
        containedPrimary: {
          borderColor: palette.primary.main,
          backgroundColor: palette.primary.main,
          color: palette.primary.contrastText,
          '&:hover': {
            borderColor: palette.secondary.dark,
            backgroundColor: palette.secondary.dark,
          },
        },
        containedSecondary: {
          borderColor: palette.secondary.dark,
          backgroundColor: palette.secondary.dark,
          color: palette.secondary.contrastText,
          '&:hover': {
            borderColor: palette.primary.main,
            backgroundColor: palette.primary.main,
          },
        },
        outlined: {
          borderWidth: '4px',
          '&:hover': {
            borderWidth: '4px',
          },
        },
        outlinedPrimary: {
          borderColor: palette.primary.main,
          color: palette.primary.main,
          '&:hover': {
            borderColor: palette.primary.main,
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
          },
        },
        outlinedSecondary: {
          borderColor: palette.secondary.dark,
          color: palette.secondary.dark,
          '&:hover': {
            borderColor: palette.secondary.dark,
            backgroundColor: palette.secondary.dark,
            color: palette.primary.contrastText,
          },
        },
      },
    },

    /*! container */
    MuiContainer: {
      defaultProps: {
        maxWidth: 'xl',
      },
      styleOverrides: {
        root: {
          paddingTop: 64,
          paddingBottom: 64,
          [defaults.breakpoints.up('md')]: {
            paddingLeft: 64,
            paddingRight: 64,
          },
          [defaults.breakpoints.down('md')]: {
            paddingTop: 32,
            paddingBottom: 32,
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: palette.gray.main,
        },
      },
    },

    /*! inputs */
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: '700',
          color: palette.secondary.dark,
          fontSize: '1.25rem',
        },
        shrink: {
          position: 'relative',
          transform: 'unset',
        },
        asterisk: {
          color: palette.primary.main,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          border: '4px solid',
          borderColor: palette.gray.light,
          minHeight: '4rem',
          marginTop: '0 !important',
          transition: 'border-color .25s ease .1s',
          '&:hover, &.Mui-focused': {
            borderColor: palette.primary.main,
          },
          '& .MuiSelect-select': {
            padding: '0',
          },
          '& fieldset': {
            border: 'none',
          },
          '&::before, &::after': {
            display: 'none',
          },
        },
        input: {
          padding: '.5rem',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
        color: 'primary',
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '.175rem',
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '.175rem',
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          '.MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        },
        select: {
          paddingLeft: '.5rem',
        },
      },
    },

    /*! tabs */
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTab-root.Mui-selected': {
            color: palette.secondary.dark,
          },
        },
      },
    },

    /*! accordion */
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: '.25rem solid',
          borderColor: palette.gray.light,
          transition: 'border-color .25s ease .1s, box-shadow .25s ease .1s',
          '&:hover': {
            borderColor: palette.primary.main,
          },
          '&:before': {
            display: 'none',
          },
          '& .MuiAccordionSummary-root': {
            minHeight: '4rem',
          },
          '&.Mui-expanded': {
            margin: '0',
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: '12px 0',
            },
          },
        },
      },
    },

    /*! card */
    MuiCard: {
      variants: [
        {
          props: { variant: 'cardHover' },
          style: {
            border: '.5rem solid',
            borderColor: palette.gray.light,
            transition: 'border-color .25s ease .1s, box-shadow .25s ease .1s',
            '&:hover': {
              borderColor: palette.primary.main,
              boxShadow: defaults.shadows[10],
            },
          },
        },
        {
          props: { variant: 'cardBorder' },
          style: {
            border: '.5rem solid',
            borderColor: palette.gray.light,
            transition: 'border-color .25s ease .1s, box-shadow .25s ease .1s',
          },
        },
      ],
      styleOverrides: {
        root: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'none',
          border: 'none',
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          '.MuiTypography-root': {
            lineHeight: 'normal',
          },
        },
        avatar: {
          marginRight: '0',
          '.MuiAvatar-root': {
            backgroundColor: palette.primary.main,
            width: '4rem',
            height: '4rem',
            fontSize: '1.75rem',
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          flex: '1',
        },
      },
    },

    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '1rem',
          justifyContent: 'flex-end',
        },
      },
    },

    /*! alerts */
    MuiAlert: {
      styleOverrides: {
        root: {},
        colorInfo: {
          backgroundColor: palette.info.light,
        },
        outlinedInfo: {
          backgroundColor: palette.info.light,
          color: palette.info.dark,
          borderColor: palette.info.main,
        },
        outlinedWarning: {
          backgroundColor: palette.warning.light,
          color: palette.warning.dark,
          borderColor: palette.warning.main,
        },
        outlinedSuccess: {
          backgroundColor: palette.success.light,
          color: palette.success.dark,
          borderColor: palette.success.main,
        },
        outlinedError: {
          backgroundColor: palette.error.light,
          color: palette.error.dark,
          borderColor: palette.error.main,
        },
      },
    },
  },
});
